import {Injectable, Inject} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {BehaviorSubject, Observable, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {User} from '../models/User';
import Swal from 'sweetalert2';

@Injectable({providedIn: 'root'})
export class AuthenticationService {
  public currentUser: Observable<User | null>;
  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };
  private currentUserSubject: BehaviorSubject<User | null>;

  constructor(private http: HttpClient, @Inject('BASE_URL') private baseUrl: string) {
    this.currentUserSubject = new BehaviorSubject<User | null>(
      localStorage.getItem('currentUser') ? JSON.parse(localStorage.getItem('currentUser')!) : null
    );
    this.currentUser = this.currentUserSubject.asObservable();
  }

  public get currentUserValue(): User | null {
    return this.currentUserSubject.value;
  }

  login(email: string, password: string): Observable<any> {
    return this.http.post<any>(this.baseUrl + 'auth/login', {email, password}, this.httpOptions)
      .pipe(
        map(user => {
          if (user && user.token) {
            localStorage.setItem('currentUser', JSON.stringify(user));
            localStorage.setItem('refreshToken', user.refreshToken);
            this.currentUserSubject.next(user);
          }
          return user;
        }),
        catchError(error => throwError(error))
      );
  }

  forgotPassword(email: string): Observable<any> {
    return this.http.post(this.baseUrl + 'users/forgot-password', { email }, { ...this.httpOptions, responseType: 'text' })
      .pipe(
        map(response => response),
        catchError(error => throwError(() => error))
      );
  }

  resetPassword(token: string, newPassword: string): Observable<any> {
    return this.http.post(this.baseUrl + 'users/reset-password', { token, newPassword }, { ...this.httpOptions, responseType: 'text' })
      .pipe(
        map(response => response),
        catchError(error => throwError(() => error))
      );
  }

  verifySecureCode(email: string, code: string): Observable<any> {
    return this.http.post<any>(this.baseUrl + `auth/verify`, { email, code })
      .pipe(
        map(user => {
          if (user && user.token) {
            localStorage.setItem('currentUser', JSON.stringify(user));
            localStorage.setItem('refreshToken', user.refreshToken);
            this.currentUserSubject.next(user);
          }
          return user;
        }),
        catchError(error => throwError(error))
      );
  }

  logout(sessionExpiredMessage?: string) {
    localStorage.removeItem('currentUser');
    localStorage.removeItem('refreshToken');
    this.currentUserSubject.next(null);
    localStorage.clear();
    sessionStorage.clear();

    if (sessionExpiredMessage) {
      Swal.fire({
        title: 'Session expired',
        text: sessionExpiredMessage,
        icon: 'warning',
        confirmButtonText: 'OK',
      }).then(() => {
        window.location.href = '/';
      });
    } else {
      window.location.href = '/';
    }
  }

  refreshToken(): Observable<any> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      this.logout('Your session has expired. Please log in again.');
      return throwError('No refresh token available');
    }

    return this.http.post<any>(this.baseUrl + 'auth/refresh', {refreshToken}, this.httpOptions)
      .pipe(
        map(response => {
          if (response && response.token) {
            const updatedUser: any = {...this.currentUserValue, token: response.token};
            localStorage.setItem('currentUser', JSON.stringify(updatedUser));
            localStorage.setItem('refreshToken', response.refreshToken);
            this.currentUserSubject.next(updatedUser);
            return response;
          }
          throw new Error('Invalid response from refresh API');
        }),
        catchError(error => {
          this.logout('Your session has expired. Please log in again.');
          return throwError(error);
        })
      );
  }

}
